import React, {useMemo, useState, useEffect} from "react";
import {useSelector} from "react-redux";
import {loggedInUserState} from "redux/userSlice";
import { userOrganizationState } from "redux/organizationSlice";
import { generateClient } from 'aws-amplify/api';
import { getCurrentUser } from 'aws-amplify/auth';
import { post } from 'aws-amplify/api';
import { userByOrganization } from 'graphql/queries';
import { createEMAILNOTIFICATIONS } from 'graphql/vendor';

import {
  TextField,
  Button,
  Box,
  Avatar,
  Collapse,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Alert,
  Chip,
} from "@mui/material";
import {Feed, Menu, List} from "semantic-ui-react"
import {SubtitleInline} from "components/shared/Typography";
import DeleteConfirmationModal from "components/shared/Modal/DeleteConfirmationModal";
import LoaderButton from "components/shared/LoaderButton";
import Modal from "components/shared/Modal"
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import DeleteIcon from '@mui/icons-material/Delete';
import RefreshIcon from '@mui/icons-material/Refresh';


const TABS = ["COMMENTS", "ACTIVITY HISTORY"]

const API = generateClient();

export default function CertificateOtherInfoSection({
  certificateData,
  handleCertificateSubmit,
  loading,
  certificateActivities,
  onDeleteCallback=()=>{},
  scanData,
}) {

  const comments = useMemo(() => {
    let unsorted = certificateData?.comments || []
    if(typeof unsorted === "string") {
      unsorted = JSON.parse(unsorted)
    }
    return unsorted?.sort((a,b) => a.time > b.time ? -1 : 1)

  }, [certificateData?.comments])

  const [addingComment, setAddingComment] = useState(false)
  const [viewTab, setViewTab] = useState("COMMENTS")
  const [newComment, setNewComment] = useState("")
  const [mentionedUsers, setMentionedUsers] = useState([])
  const [availableUsers, setAvailableUsers] = useState([])
  const [showMentionDropdown, setShowMentionDropdown] = useState(false)
  const [mentionSearch, setMentionSearch] = useState("")
  const [mentionCursorPosition, setMentionCursorPosition] = useState(null)
  const [selectedMentionIndex, setSelectedMentionIndex] = useState(0)
  const [commentToDelete, setCommentToDelete] = useState()
  const { loggedInUser } = useSelector(loggedInUserState)
  const { userOrganization } = useSelector(userOrganizationState)
  const [showActivityDetail, setShowActivityDetail] = useState({ "0": true })
  const [deleteCertificate, setDeleteCertificate] = useState(false);
  const [deleting, setDeleting] = useState(false)
  const commentInputRef = React.useRef(null)

  // Rescan certificate states
  const [showRescanDialog, setShowRescanDialog] = useState(false)
  const [rescanning, setRescanning] = useState(false)
  const [rescanError, setRescanError] = useState(null)
  const [rescanSuccess, setRescanSuccess] = useState(false)


  function onCancelDelete () {
    setDeleteCertificate(false);
  }

  async function onDeleteCertificate () {
    setDeleting(true)
    await onDeleteCallback();
    setDeleting(false)
    setDeleteCertificate(false)
  }

  // Fetch available users for mentions
  useEffect(() => {
    const fetchUsers = async () => {
      if (!userOrganization?.id) return;
      
      try {
        // Get current user from Amplify Auth as fallback
        let authUser = null;
        try {
          const currentAuthUser = await getCurrentUser();
          authUser = {
            email: currentAuthUser.signInDetails?.loginId || currentAuthUser.username,
            id: currentAuthUser.userId || currentAuthUser.username
          };
        } catch (e) {
          // Silently handle if auth user is not available
        }
        
        const result = await API.graphql({
          query: userByOrganization,
          variables: {
            organizationID: userOrganization.id
          }
        });
        
        let users = result.data?.userByOrganization?.items?.filter(
          user => user.status === 'ACTIVE'
        ) || [];
        
        // Determine the current user email (from Redux or Auth)
        const currentUserEmail = loggedInUser?.email || authUser?.email;
        
        // Check if current user is in the list
        const currentUserInList = users.find(u => 
          u.email?.toLowerCase() === currentUserEmail?.toLowerCase()
        );
        
        if (!currentUserInList && currentUserEmail) {
          const currentUser = {
            id: loggedInUser?.id || authUser?.id || `current-user-${Date.now()}`,
            email: currentUserEmail,
            firstName: loggedInUser?.firstName || currentUserEmail.split('@')[0].split('.')[0],
            lastName: loggedInUser?.lastName || currentUserEmail.split('@')[0].split('.')[1] || '',
            status: 'ACTIVE'
          };
          users = [currentUser, ...users]; // Add current user at the beginning
        }
        setAvailableUsers(users);
      } catch (error) {
        console.error('Error fetching users:', error);
      }
    };
    
    fetchUsers();
  }, [userOrganization?.id, loggedInUser]);

  // Filter users based on mention search
  const filteredUsers = React.useMemo(() => {
    if (!mentionSearch) {
      return availableUsers;
    }
    
    const searchLower = mentionSearch.toLowerCase();
    const filtered = availableUsers.filter(user => {
      const fullName = `${user.firstName || ''} ${user.lastName || ''}`.toLowerCase().trim();
      const email = user.email?.toLowerCase() || '';
      const emailUsername = email.split('@')[0];
      
      // Check various combinations for matching
      return fullName.includes(searchLower) || 
             user.firstName?.toLowerCase().includes(searchLower) ||
             user.lastName?.toLowerCase().includes(searchLower) ||
             email.includes(searchLower) ||
             emailUsername.includes(searchLower) ||
             fullName.startsWith(searchLower) ||
             email.startsWith(searchLower);
    });
    
    return filtered;
  }, [availableUsers, mentionSearch]);

  // Handle comment input changes to detect @ mentions
  const handleCommentChange = (event) => {
    const value = event.target.value;
    const cursorPos = event.target.selectionStart;
    setNewComment(value);
    
    // Check if we should show mention dropdown
    const lastAtSymbol = value.lastIndexOf('@', cursorPos - 1);
    
    if (lastAtSymbol !== -1) {
      const textAfterAt = value.substring(lastAtSymbol + 1, cursorPos);
      const hasSpaceAfterAt = textAfterAt.includes(' ');
      
      if (!hasSpaceAfterAt) {
        setShowMentionDropdown(true);
        setMentionSearch(textAfterAt);
        setMentionCursorPosition(lastAtSymbol);
        setSelectedMentionIndex(0);
      } else {
        setShowMentionDropdown(false);
      }
    } else {
      setShowMentionDropdown(false);
    }
  };

  // Handle selecting a user from mention dropdown
  const handleSelectMention = (user) => {
    if (mentionCursorPosition !== null) {
      const beforeMention = newComment.substring(0, mentionCursorPosition);
      const afterMention = newComment.substring(mentionCursorPosition + mentionSearch.length + 1);
      const userName = `${user.firstName} ${user.lastName}`.trim();
      const newCommentText = `${beforeMention}@${userName} ${afterMention}`;
      
      setNewComment(newCommentText);
      
      // Add to mentioned users if not already there
      if (!mentionedUsers.find(u => u.id === user.id)) {
        setMentionedUsers([...mentionedUsers, user]);
      }
    }
    
    setShowMentionDropdown(false);
    setMentionSearch("");
    setMentionCursorPosition(null);
    
    // Focus back on input
    if (commentInputRef.current) {
      commentInputRef.current.focus();
    }
  };

  // Handle keyboard navigation in mention dropdown
  const handleKeyDown = (event) => {
    if (!showMentionDropdown) return;
    
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        setSelectedMentionIndex(prev => 
          prev < filteredUsers.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        event.preventDefault();
        setSelectedMentionIndex(prev => prev > 0 ? prev - 1 : 0);
        break;
      case 'Enter':
        event.preventDefault();
        if (filteredUsers[selectedMentionIndex]) {
          handleSelectMention(filteredUsers[selectedMentionIndex]);
        }
        break;
      case 'Escape':
        setShowMentionDropdown(false);
        break;
      default:
        break;
    }
  };

  async function onSaveComment() {
    if(newComment) {
      const commentsToSave = [...comments]
      const firstName = loggedInUser?.firstName || ""
      const lastName = loggedInUser?.lastName ? ` ${loggedInUser.lastName}` : ""
      
      // Extract mentioned users from comment text
      const mentionRegex = /@([^\s]+(?:\s+[^\s]+)?)/g;
      const extractedMentions = [];
      let match;
      
      while ((match = mentionRegex.exec(newComment)) !== null) {
        const mentionName = match[1].trim();
        const mentionedUser = availableUsers.find(user => {
          const fullName = `${user.firstName} ${user.lastName}`.trim();
          return fullName === mentionName || 
                 user.firstName === mentionName ||
                 user.lastName === mentionName;
        });
        
        if (mentionedUser && !extractedMentions.find(u => u.id === mentionedUser.id)) {
          extractedMentions.push(mentionedUser);
        }
      }
      
      // Format comment with mentions
      let formattedComment = newComment;
      extractedMentions.forEach(user => {
        const userName = `${user.firstName} ${user.lastName}`.trim();
        formattedComment = formattedComment.replace(
          new RegExp(`@${userName}`, 'g'),
          `<span class="mention">@${userName}</span>`
        );
      });
      
      commentsToSave.push({
        time: Date.now(),
        content: formattedComment,
        author: `${firstName}${lastName}`,
        mentions: extractedMentions.map(u => u.id)
      })
      
      const result = await handleCertificateSubmit({comments: JSON.stringify(commentsToSave)})
      
      if(result?.success) {
        // Send notifications to mentioned users
        if (extractedMentions.length > 0) {
          await sendMentionNotifications(extractedMentions);
        }
        onCancelComment()
      }
    } else {
      onCancelComment()
    }
  }

  async function sendMentionNotifications(usersToNotify) {
    if (!usersToNotify || usersToNotify.length === 0) return;
    
    try {
      const createNotificationMutation = /* GraphQL */ `
        mutation CreateNotification($input: CreateNOTIFICATIONInput!) {
          createNOTIFICATION(input: $input) {
            id
          }
        }
      `;
      
      for (const user of usersToNotify) {
        // Create in-app notification
        await API.graphql({
          query: createNotificationMutation,
          variables: {
            input: {
              type: 'COMMENT_MENTION',
              status: 'UNREAD',
              title: 'You were mentioned in a comment',
              message: `${loggedInUser?.firstName} ${loggedInUser?.lastName} mentioned you in a comment`,
              userID: user.id,
              organizationID: userOrganization?.id,
              certificateID: certificateData?.id,
              metadata: JSON.stringify({
                comment: newComment,
                authorName: `${loggedInUser?.firstName} ${loggedInUser?.lastName}`.trim()
              })
            }
          }
        });
        
        // Send email notification
        if (user.email) {
          try {
            const emailResult = await API.graphql({
              query: createEMAILNOTIFICATIONS,
              variables: {
                input: {
                  recipients: [user.email],  // Must be an array
                  action: 'Custom',  // Required for email Lambda to process
                  subject: `You were mentioned in a certificate comment`,
                  content: `${loggedInUser?.firstName} ${loggedInUser?.lastName} mentioned you in a comment:\n\n"${newComment}"\n\nView the certificate to respond.`,
                  greeting: `Hi ${user.firstName},`,
                  signature: `Best regards,\n${userOrganization?.name || 'MANACERT Team'}`,
                  vendorName: user.firstName || user.email?.split('@')[0] || 'User',  // Lambda uses this for greeting
                  clientName: userOrganization?.name || 'MANACERT'  // Add client name
                }
              }
            });
            console.log('Email notification created:', emailResult);
          } catch (emailError) {
            console.error('Error creating email notification:', emailError);
          }
        }
      }
    } catch (error) {
      console.error('Error sending mention notifications:', error);
    }
  }

  function onCancelComment() {
    setAddingComment(false)
    setNewComment("")
    setMentionedUsers([])
    setShowMentionDropdown(false)
    setMentionSearch("")
    setMentionCursorPosition(null)
    setSelectedMentionIndex(0)
  }

  function onDeleteComment(timestamp) {
    setCommentToDelete(timestamp)
  }

  async function confirmDeleteComment() {
    const commentsToSave = [...comments].filter(comment => comment.time !== commentToDelete)
    const result = await handleCertificateSubmit({comments: JSON.stringify(commentsToSave)})
    if(result?.success) {
      onCancelComment()
      setCommentToDelete(undefined)
    }
  }

  // Handle rescan certificate
  async function handleRescanCertificate() {
    setRescanning(true);
    setRescanError(null);
    setRescanSuccess(false);

    try {
      console.log('Initiating certificate rescan:', certificateData?.id);

      const restOperation = post({
        apiName: 'FileUploadApi',
        path: '/upload/rescan',
        options: {
          body: {
            certificateId: certificateData?.id
          }
        }
      });

      const { body } = await restOperation.response;
      const response = await body.json();

      console.log('Rescan response:', response);

      if (response.success) {
        setRescanSuccess(true);
        setRescanError(null);

        // Auto-close dialog after 2 seconds and refresh page
        setTimeout(() => {
          setShowRescanDialog(false);
          window.location.reload();
        }, 2000);
      } else {
        throw new Error(response.error || 'Failed to initiate rescan');
      }
    } catch (error) {
      console.error('Rescan error:', error);
      setRescanError(error.message || 'Failed to rescan certificate. Please try again.');
      setRescanSuccess(false);
    } finally {
      setRescanning(false);
    }
  }

  function handleOpenRescanDialog() {
    setShowRescanDialog(true);
    setRescanError(null);
    setRescanSuccess(false);
  }

  function handleCloseRescanDialog() {
    if (!rescanning) {
      setShowRescanDialog(false);
      setRescanError(null);
      setRescanSuccess(false);
    }
  }

  // Get extraction method display info
  const extractionMethodInfo = useMemo(() => {
    const method = certificateData?.extractionMethod || 'OCR';
    const model = certificateData?.extractionModel;

    const methodLabels = {
      'OCR': { label: 'OCR Only', color: 'default', description: 'Textract OCR extraction' },
      'CLAUDE': { label: 'AI Primary', color: 'primary', description: 'Claude AI extraction' },
      'HYBRID': { label: 'Hybrid', color: 'secondary', description: 'Claude AI + OCR merged' },
      'MANUAL': { label: 'Manual', color: 'warning', description: 'Manually entered' }
    };

    return {
      ...methodLabels[method] || methodLabels['OCR'],
      model: model || 'N/A'
    };
  }, [certificateData?.extractionMethod, certificateData?.extractionModel]);

  return (
    <Box className={"other-info-section"}>
      <DeleteConfirmationModal
        message={"certificate"}
        open={deleteCertificate}
        onClose={onCancelDelete}
        onDelete={onDeleteCertificate}
        loading={deleting}
      />
      <Modal
        open={!!commentToDelete}
        title={"Delete comment?"}
        content={"Once deleted, you will not be able to get it back."}
        actions={
          <Box>
            <Button onClick={()=>setCommentToDelete(undefined)}>Cancel</Button>
            <LoaderButton loading={loading} variant={"contained"} onClick={confirmDeleteComment}>Delete</LoaderButton>
          </Box>
        }
      />
      {/* Rescan Confirmation Dialog */}
      <Dialog
        open={showRescanDialog}
        onClose={handleCloseRescanDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Rescan Certificate for Re-Extraction
        </DialogTitle>
        <DialogContent>
          {rescanSuccess ? (
            <Alert severity="success" sx={{ mb: 2 }}>
              Certificate rescan initiated successfully! The page will refresh shortly to show the processing status.
            </Alert>
          ) : (
            <>
              <DialogContentText sx={{ mb: 2 }}>
                This will reprocess the existing ACORD 25 file through the extraction system without uploading a new file.
              </DialogContentText>

              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="body2" fontWeight="bold" gutterBottom>
                  Current Extraction Method
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <Chip
                    label={extractionMethodInfo.label}
                    color={extractionMethodInfo.color}
                    size="small"
                  />
                  {extractionMethodInfo.model !== 'N/A' && (
                    <Typography variant="caption" color="text.secondary">
                      Model: {extractionMethodInfo.model}
                    </Typography>
                  )}
                </Box>
                <Typography variant="caption" color="text.secondary">
                  {extractionMethodInfo.description}
                </Typography>
              </Alert>

              <Alert severity="warning" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  <strong>Important:</strong> The rescan will use your organization's <strong>current</strong> AI-Powered Certificate Extraction settings, which may be different from when this certificate was originally processed.
                </Typography>
              </Alert>

              {certificateData?.reprocessedCount > 0 && (
                <Alert severity="info">
                  <Typography variant="body2">
                    This certificate has been rescanned <strong>{certificateData.reprocessedCount}</strong> time{certificateData.reprocessedCount > 1 ? 's' : ''} previously.
                  </Typography>
                </Alert>
              )}

              {rescanError && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  {rescanError}
                </Alert>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseRescanDialog} disabled={rescanning || rescanSuccess}>
            Cancel
          </Button>
          <LoaderButton
            onClick={handleRescanCertificate}
            loading={rescanning}
            variant="contained"
            color="primary"
            disabled={rescanSuccess}
            startIcon={!rescanning && !rescanSuccess && <RefreshIcon />}
          >
            {rescanSuccess ? 'Success!' : 'Confirm Rescan'}
          </LoaderButton>
        </DialogActions>
      </Dialog>

      <Box sx={{mt: 1}}>
        <Menu text className="status-filter" style={{alignItems: "center", marginLeft: 0, marginTop: 0}}>
          <Menu.Item>Showing:</Menu.Item>
          {
            TABS.map(tab => (
              <Menu.Item active={viewTab === tab} onClick={() => setViewTab(tab)} key={tab}>
                <SubtitleInline>{tab}</SubtitleInline>
              </Menu.Item>
            ))
          }
          {(certificateData?.acord25File || scanData?.acord25File) && (
            <Button
              variant="text"
              color="primary"
              startIcon={<RefreshIcon />}
              style={{margin: "0 0.5rem 0 auto"}}
              onClick={handleOpenRescanDialog}
              disabled={rescanning}
            >
              Rescan Certificate
            </Button>
          )}
          <Button variant="text" color="error" startIcon={<DeleteIcon />} disabled={deleteCertificate}  style={{margin: "0 1rem 0 0"}} onClick={() => setDeleteCertificate(true)}>Delete Certificate</Button>
        </Menu>
      </Box>
      {
        viewTab === TABS[0] &&
        <>
          <Box sx={{ position: 'relative' }}>
            <TextField
              inputRef={commentInputRef}
              sx={{mt:1}}
              fullWidth
              multiline
              rows={addingComment ? 4 : 1}
              placeholder={"Add a new comment... Use @ to mention users"}
              name="comments"
              value={newComment}
              onChange={handleCommentChange}
              onKeyDown={handleKeyDown}
              onFocus={()=>setAddingComment(true)}
            />
            {showMentionDropdown && filteredUsers.length > 0 && (
              <Box
                sx={{
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  right: 0,
                  mt: 0.5,
                  bgcolor: 'background.paper',
                  border: 1,
                  borderColor: 'divider',
                  borderRadius: 1,
                  boxShadow: 2,
                  maxHeight: 200,
                  overflowY: 'auto',
                  zIndex: 1000,
                }}
              >
                {filteredUsers.map((user, index) => (
                  <Box
                    key={user.id}
                    onClick={() => handleSelectMention(user)}
                    sx={{
                      px: 2,
                      py: 1,
                      cursor: 'pointer',
                      backgroundColor: index === selectedMentionIndex ? 'action.selected' : 'transparent',
                      '&:hover': {
                        backgroundColor: 'action.hover',
                      },
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                    }}
                  >
                    <Avatar sx={{ width: 24, height: 24, fontSize: '0.75rem' }}>
                      {user.firstName?.charAt(0) || user.email?.charAt(0)?.toUpperCase()}
                      {user.lastName?.charAt(0) || ''}
                    </Avatar>
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {user.firstName || user.email?.split('@')[0]} {user.lastName || ''}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {user.email}
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </Box>
            )}
          </Box>
          {
            addingComment &&
            <Box sx={{mt: 1, textAlign: "right"}}>
              <Button variant={"outlined"} sx={{mr: 1}} onClick={onCancelComment}>Cancel</Button>
              <LoaderButton variant={"contained"} loading={loading} onClick={onSaveComment}>Save</LoaderButton>
            </Box>
          }
          <Box
            sx={{
              mt: 2,
              maxHeight: "1000px",
              overflowY: "scroll",
              "&::-webkit-scrollbar": {
                width: "2px"
              }
            }}
          >
            <Feed>
              {
                !!comments?.length && comments.map(comment => {
                  const nameParts = String(comment.author).split(/\s/)
                  const commentTime = new Date(comment.time)
                  return (
                    <Feed.Event key={comment.time}>
                      <Feed.Label>
                        <Avatar sx={{ width: 35, height: 35, mt: "0.5em" }}>
                          {
                            comment.author &&
                            (
                              String(nameParts?.[0]).charAt(0).toUpperCase()
                              + String(nameParts?.[1] || "").charAt(0).toUpperCase()
                            )
                          }
                        </Avatar>
                      </Feed.Label>
                      <Feed.Content>
                        <Feed.Summary>
                          {comment.author}
                          <Feed.Date>{`${commentTime.toLocaleDateString()} at ${commentTime.toLocaleTimeString()}`}</Feed.Date>
                        </Feed.Summary>
                        <Feed.Extra text>
                          <div dangerouslySetInnerHTML={{ 
                            __html: comment.content?.replace(
                              /<span class="mention">(@[^<]+)<\/span>/g, 
                              '<strong style="color: #1976d2;">$1</strong>'
                            ) || comment.content 
                          }} />
                        </Feed.Extra>
                        <Feed.Meta>
                          <Feed.Like
                            className={"delete-btn"}
                            onClick={()=>onDeleteComment(comment.time)}
                          >Delete</Feed.Like>
                        </Feed.Meta>
                      </Feed.Content>
                    </Feed.Event>
                  )
                })
              }
            </Feed>
          </Box>
        </>
      }
      {
        viewTab === TABS[1] && !!certificateActivities?.length &&
        <Box
          sx={{
            overflowY: "scroll",
            maxHeight: "1200px",
            "&::-webkit-scrollbar": {
              width: "2px"
            }
          }}
        >
          <Feed>
            {
              certificateActivities.map((activity, index) => {
                let updates = JSON.parse(activity.field || "{}")
                const certificateUpdates = updates.certificate;
                const coverageUpdates = updates.coverages;
                const fileUpdates = updates.files;
                const projectUpdates = updates.project;
                const scanMetadata = updates.scan;
                const updateSections = []
                let updatedSectionDesc;
                certificateUpdates && updateSections.push("information");
                coverageUpdates && updateSections.push("coverages");
                fileUpdates && updateSections.push("files");
                projectUpdates && updateSections.push("project");

                if(updateSections.length === 1) {
                  updatedSectionDesc = updateSections[0]
                } else if(updateSections.length === 2) {
                  updatedSectionDesc = `${updateSections[0]} and ${updateSections[1]}`
                } else if(updateSections.length === 2) {
                  updatedSectionDesc = `${updateSections[0]}, ${updateSections[1]} and ${updateSections[2]}`
                }

                let activityDescription;
                if (activity.action === "CREATE") {
                  activityDescription = "created certificate";
                } else if (activity.action === "SCAN_SUBMITTED") {
                  activityDescription = "submitted scan result";
                } else {
                  activityDescription = `updated certificate's ${updatedSectionDesc}`;
                }

                return (
                  <Feed.Event key={"activity-" + index}>
                    <Feed.Label>
                      <Avatar>
                        {
                          String(activity.userFullName)
                            .split(" ")
                            .map(part => part.charAt(0))
                            .join("")
                            .toUpperCase()
                        }
                      </Avatar>
                    </Feed.Label>
                    <Feed.Content>
                      <Feed.Date>{(new Date(activity.createdAt)).toLocaleString("en-us")}</Feed.Date>
                      <Feed.Summary style={{display: "flex", alignItems: "baseline"}}>
                        <Box>
                          <span style={{fontWeight: "600"}}>{activity.userFullName}</span>
                          {` ${activityDescription}`}
                        </Box>
                        <Button
                          size="small"
                          sx={{ml: "auto", flexShrink: 0}}
                          onClick={()=>setShowActivityDetail({...showActivityDetail, [index]: !showActivityDetail[index]})}
                        >
                          {`${!!showActivityDetail[index] ? "Hide" : "Show"} details`}
                        </Button>
                      </Feed.Summary>
                      {
                        !!Object.keys(updates).length &&
                        <Collapse in={!!showActivityDetail[index]}>
                          <Feed.Extra text>
                            <List bulleted={true}>
                              {
                                scanMetadata ? (
                                  // Display scan metadata as simple key-value pairs
                                  Object.entries(scanMetadata).map(([key, value]) => (
                                    <List.Item key={`${activity.id}_${key}`}>
                                      <p style={{textTransform: "capitalize", fontWeight: "600"}}>
                                        {key.replace(/([A-Z])/g, ' $1').trim()}
                                      </p>
                                      <p style={{marginLeft: "1em"}}>{value}</p>
                                    </List.Item>
                                  ))
                                ) : (
                                  // Display certificate/coverage/file updates as prev -> next
                                  Object.entries({
                                    ...(certificateUpdates || {}),
                                    ...(coverageUpdates || {}),
                                    ...(fileUpdates || {}),
                                    ...(projectUpdates || {}),
                                  }).map(([fieldName, updates]) => {
                                    if(fieldName !== "requestedNewValidCertificateURL")  {
                                    return (
                                      <List.Item key={`${activity.id}_${fieldName}`}>
                                        <p style={{textTransform: "capitalize", fontWeight: "600"}}>{fieldName}</p>
                                        <Box sx={{ display: "flex", alignItems: "center" }}>
                                        {
                                          updates.prev
                                            ? <p style={{flexBasis: "50%", flexGrow: 0, overflowWrap: "anywhere"}}>{updates.prev}</p>
                                            : <p style={{color: "darkgray"}}>{"blank"}</p>
                                        }
                                        <ArrowForwardIcon sx={{fontSize: 16, ml: 1, mr: 1}}/>
                                        {
                                          updates.next
                                            ? <p style={{flexBasis: "50%", flexGrow: 0, overflowWrap: "anywhere"}}>{updates.next}</p>
                                            : <p style={{color: "darkgray"}}>{"blank"}</p>
                                        }
                                        </Box>
                                      </List.Item>
                                    )
                                    } else {
                                      return (
                                        <List.Item key={`${activity.id}_${fieldName}`}>
                                          <p style={{textTransform: "capitalize", fontWeight: "600"}}><a href={updates.next}>{fieldName}</a></p>
                                        </List.Item>
                                      )
                                  }
                                  })
                                )
                              }
                            </List>
                          </Feed.Extra>
                        </Collapse>
                      }
                    </Feed.Content>
                  </Feed.Event>
                )
              })
            }
          </Feed>
        </Box>
      }
    </Box>
  )
}
